import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, LogOut, Inbox, Images, Trash2, Check, RotateCcw,
  Plus, Star, Mail, Pencil, X, CalendarDays, Upload, Clock,
  ClipboardList, Users, ShieldCheck, Package, Tag, ShoppingBag,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  getInquiries, setInquiryStatus, deleteInquiry,
  getGallery, createGalleryItem, updateGalleryItem, deleteGalleryItem,
  getConsignments, createConsignment, updateConsignment, deleteConsignment,
  getProducts, createProduct, updateProduct, deleteProduct,
  uploadImage,
  adminGetSlots, adminCreateSlot, adminBulkCreateSlots, adminSetSlotStatus, adminDeleteSlot, getSlotsConfig,
  adminGetRequests, adminSetRequestStatus, adminDeleteRequest,
  adminGetTeam, adminCreateTeamMember, adminDeleteTeamMember,
} from "../lib/api";
import { adminGetOrders, adminSetOrderStatus, adminDeleteOrder, getOrdersConfig } from "../lib/customerApi";
import ReplyButton from "../components/ReplyButton";

const EMPTY_ITEM = { title: "", subtitle: "", grade: "", price: "", image: "", featured: false };

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("requests");

  if (!ready)
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-gold" />
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <main className="min-h-screen pt-24 pb-24 px-5 sm:px-8" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
              [ Dashboard ]
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold uppercase tracking-tighter mt-2">
              Control Room
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="font-mono text-xs uppercase tracking-[0.12em] text-zinc-400 hover:text-white"
              data-testid="admin-view-site"
            >
              View Site
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-5 py-2.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#27272a]">
          {[
            { k: "requests", label: "Requests", icon: ClipboardList },
            { k: "orders", label: "Service Orders", icon: Package },
            { k: "slots", label: "Studio Slots", icon: CalendarDays },
            { k: "consignments", label: "Consignments", icon: Tag },
            { k: "shop", label: "Shop", icon: ShoppingBag },
            { k: "inquiries", label: "Inquiries", icon: Inbox },
            { k: "gallery", label: "Gallery", icon: Images },
            { k: "team", label: "Team", icon: Users },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.12em] border-b-2 -mb-px transition-colors ${
                tab === t.k
                  ? "border-gold text-gold"
                  : "border-transparent text-zinc-500 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "requests" && <RequestsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "inquiries" && <InquiriesTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "consignments" && <ConsignmentsTab />}
        {tab === "shop" && <ProductsTab />}
        {tab === "slots" && <SlotsTab />}
        {tab === "team" && <TeamTab />}
      </div>
    </main>
  );
}

function InquiriesTab() {
  const [items, setItems] = useState(null);

  const load = () => getInquiries().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggle = async (it) => {
    const next = it.status === "new" ? "handled" : "new";
    try {
      await setInquiryStatus(it.id, next);
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status: next } : x)));
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await deleteInquiry(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Inquiry deleted.");
    } catch { toast.error("Delete failed."); }
  };

  if (!items) return <Loader2 className="animate-spin text-gold" />;
  if (!items.length)
    return <p className="text-zinc-500 font-mono text-sm">No inquiries yet.</p>;

  return (
    <div className="space-y-4" data-testid="inquiries-list">
      {items.map((it) => (
        <div
          key={it.id}
          className={`border p-6 ${it.status === "handled" ? "border-[#27272a] opacity-70" : "border-gold/30 bg-gold/5"}`}
          data-testid={`inquiry-${it.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-xl font-semibold">{it.name}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${it.status === "handled" ? "bg-[#27272a] text-zinc-400" : "bg-gold text-black"}`}>
                  {it.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1.5 hover:text-gold">
                  <Mail size={13} /> {it.email}
                </a>
                <span className="font-mono text-xs text-gold">{it.service}</span>
                <span className="font-mono text-xs text-zinc-600">
                  {new Date(it.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-zinc-300 mt-3 text-sm leading-relaxed max-w-2xl">{it.message}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ReplyButton
                email={it.email}
                name={it.name}
                defaultSubject={`Re: Your enquiry — ${it.service || "South East Collectibles"}`}
                greeting={`Hi ${it.name || "there"},\n\nThank you for getting in touch about ${it.service || "our services"}.\n\n\n\n— South East Collectibles`}
                testid={`inquiry-reply-${it.id}`}
              />
              <button
                onClick={() => toggle(it)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors"
                data-testid={`inquiry-toggle-${it.id}`}
              >
                {it.status === "new" ? <><Check size={13} /> Mark Handled</> : <><RotateCcw size={13} /> Reopen</>}
              </button>
              <button
                onClick={() => remove(it.id)}
                className="h-9 w-9 grid place-items-center rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors"
                data-testid={`inquiry-delete-${it.id}`}
                aria-label="Delete inquiry"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => getGallery().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Photo uploaded.");
    } catch {
      toast.error("Upload failed. Use an image under 8MB.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "featured" ? e.target.checked : e.target.value }));

  const reset = () => { setForm(EMPTY_ITEM); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image) {
      toast.error("Title and image URL are required.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const updated = await updateGalleryItem(editId, form);
        setItems((arr) => arr.map((x) => (x.id === editId ? updated : x)));
        toast.success("Item updated.");
      } else {
        const created = await createGalleryItem(form);
        setItems((arr) => [created, ...arr.filter((x) => x.id !== created.id)]);
        toast.success("Item added.");
      }
      reset();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const edit = (it) => {
    setEditId(it.id);
    setForm({ title: it.title, subtitle: it.subtitle || "", grade: it.grade || "", price: it.price || "", image: it.image, featured: !!it.featured });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await deleteGalleryItem(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      if (editId === id) reset();
      toast.success("Item removed.");
    } catch { toast.error("Delete failed."); }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="gallery-admin">
      <div className="lg:col-span-4">
        <form onSubmit={submit} className="border border-[#27272a] p-6 bg-[#121212] sticky top-24" data-testid="gallery-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-semibold">
              {editId ? "Edit Item" : "Add Item"}
            </h3>
            {editId && (
              <button type="button" onClick={reset} className="text-zinc-500 hover:text-white" data-testid="gallery-cancel-edit">
                <X size={18} />
              </button>
            )}
          </div>
          <div className="space-y-4">
            <input className={inputClass} placeholder="Title *" value={form.title} onChange={update("title")} data-testid="gallery-title" />
            <input className={inputClass} placeholder="Subtitle (e.g. Consignment Sale)" value={form.subtitle} onChange={update("subtitle")} data-testid="gallery-subtitle" />
            <div className="grid grid-cols-2 gap-4">
              <input className={inputClass} placeholder="Grade (PSA 10)" value={form.grade} onChange={update("grade")} data-testid="gallery-grade" />
              <input className={inputClass} placeholder="Price (£1,850)" value={form.price} onChange={update("price")} data-testid="gallery-price" />
            </div>
            <input className={inputClass} placeholder="Image URL *" value={form.image} onChange={update("image")} data-testid="gallery-image" />
            <div>
              <label className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] cursor-pointer transition-colors">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload Photo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={onUpload}
                  data-testid="gallery-upload"
                />
              </label>
              {form.image && (
                <div className="mt-3 aspect-video border border-[#27272a] overflow-hidden">
                  <img src={form.image} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={update("featured")} className="accent-[#ebb323]" data-testid="gallery-featured" />
              Featured (larger tile)
            </label>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60"
            data-testid="gallery-save"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <>{editId ? <Pencil size={14} /> : <Plus size={14} />} {editId ? "Save Changes" : "Add To Vault"}</>}
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {!items ? (
          <Loader2 className="animate-spin text-gold" />
        ) : !items.length ? (
          <p className="text-zinc-500 font-mono text-sm">No gallery items yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="border border-[#27272a] overflow-hidden" data-testid={`gallery-admin-item-${it.id}`}>
                <div className="relative aspect-[4/3]">
                  <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                  {it.featured && (
                    <span className="absolute top-3 left-3 bg-gold text-black p-1.5"><Star size={12} /></span>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-display font-semibold">{it.title}</h4>
                  <p className="font-mono text-[11px] text-zinc-500 uppercase tracking-[0.1em] mt-1">
                    {it.grade} {it.price && `· ${it.price}`}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => edit(it)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#3a3a3a] hover:border-gold hover:text-gold py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`gallery-edit-${it.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => remove(it.id)} className="h-8 w-8 grid place-items-center border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`gallery-delete-${it.id}`} aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  open: "bg-[#27272a] text-zinc-300",
  booked: "bg-green-600 text-white",
  held: "bg-gold text-black",
  confirmed: "bg-green-600 text-white",
  closed: "bg-red-900/60 text-red-300",
};

const EMPTY_CONSIGNMENT = { title: "", player: "", grade: "", price: "", description: "", status: "available", images: [], featured: false };

const EMPTY_PRODUCT = { title: "", category: "", price: "", description: "", status: "available", images: [], featured: false };

function ProductsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => getProducts().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setForm((fm) => ({ ...fm, images: [...fm.images, ...urls] }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded.`);
    } catch { toast.error("Upload failed. Use images under 8MB."); }
    finally { setUploading(false); }
  };
  const removeImage = (url) => setForm((fm) => ({ ...fm, images: fm.images.filter((u) => u !== url) }));

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "featured" ? e.target.checked : e.target.value }));

  const reset = () => { setForm(EMPTY_PRODUCT); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required."); return; }
    if (!form.images.length) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    try {
      if (editId) {
        const updated = await updateProduct(editId, form);
        setItems((arr) => arr.map((x) => (x.id === editId ? updated : x)));
        toast.success("Product updated.");
      } else {
        const created = await createProduct(form);
        setItems((arr) => [created, ...arr.filter((x) => x.id !== created.id)]);
        toast.success("Product published to the shop.");
      }
      reset();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const edit = (it) => {
    setEditId(it.id);
    setForm({ title: it.title, category: it.category || "", price: it.price || "", description: it.description || "", status: it.status || "available", images: it.images || [], featured: !!it.featured });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await deleteProduct(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      if (editId === id) reset();
      toast.success("Product removed.");
    } catch { toast.error("Delete failed."); }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="shop-admin">
      <div className="lg:col-span-4">
        <form onSubmit={submit} className="border border-[#27272a] p-6 bg-[#121212] sticky top-24" data-testid="product-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-semibold">{editId ? "Edit Product" : "Add Product"}</h3>
            {editId && (
              <button type="button" onClick={reset} className="text-zinc-500 hover:text-white" data-testid="product-cancel-edit"><X size={18} /></button>
            )}
          </div>
          <div className="space-y-4">
            <input className={inputClass} placeholder="Title * (e.g. 2023 Mbappé Prizm PSA 10)" value={form.title} onChange={update("title")} data-testid="product-title" />
            <div className="grid grid-cols-2 gap-4">
              <input className={inputClass} placeholder="Category (Slab, Sealed…)" value={form.category} onChange={update("category")} data-testid="product-category" />
              <input className={inputClass} placeholder="Price (£75 or POA)" value={form.price} onChange={update("price")} data-testid="product-price" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Status</label>
              <select className={`${inputClass} cursor-pointer`} value={form.status} onChange={update("status")} data-testid="product-status-select">
                <option value="available" className="bg-[#121212]">Available</option>
                <option value="sold_out" className="bg-[#121212]">Sold Out</option>
              </select>
            </div>
            <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Description (optional)" value={form.description} onChange={update("description")} data-testid="product-description" />
            <div>
              <label className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] cursor-pointer transition-colors">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload Photos"}
                <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={onUpload} data-testid="product-upload" />
              </label>
              {form.images.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="product-image-previews">
                  {form.images.map((url) => (
                    <div key={url} className="relative h-20 w-20 border border-[#27272a] overflow-hidden group">
                      <img src={url} alt="preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(url)} className="absolute top-0.5 right-0.5 h-5 w-5 grid place-items-center rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors" aria-label="Remove photo" data-testid="product-image-remove"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={update("featured")} className="accent-[#ebb323]" data-testid="product-featured" />
              Featured
            </label>
          </div>
          <button type="submit" disabled={saving} className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="product-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <>{editId ? <Pencil size={14} /> : <Plus size={14} />} {editId ? "Save Changes" : "Publish Product"}</>}
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {!items ? (
          <Loader2 className="animate-spin text-gold" />
        ) : !items.length ? (
          <p className="text-zinc-500 font-mono text-sm">No products yet. Add your first one on the left — it appears in the shop instantly.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="border border-[#27272a] overflow-hidden" data-testid={`product-admin-item-${it.id}`}>
                <div className="relative aspect-[4/3] bg-[#0e0e0e]">
                  {it.images?.[0] ? <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><ShoppingBag size={26} /></div>}
                  <span className={`absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-1 font-semibold ${it.status === "sold_out" ? "bg-red-900/70 text-red-200" : "bg-gold text-black"}`}>{it.status === "sold_out" ? "Sold Out" : "Available"}</span>
                  {it.images?.length > 1 && <span className="absolute bottom-2 right-2 font-mono text-[10px] text-zinc-300 bg-black/60 px-2 py-0.5">{it.images.length} photos</span>}
                  {it.featured && <span className="absolute top-3 right-3 bg-gold text-black p-1.5"><Star size={12} /></span>}
                </div>
                <div className="p-4">
                  <h4 className="font-display font-semibold truncate">{it.title}</h4>
                  <p className="font-mono text-[11px] text-zinc-500 uppercase tracking-[0.1em] mt-1">
                    {it.category} {it.price && `· ${it.price}`}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => edit(it)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#3a3a3a] hover:border-gold hover:text-gold py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`product-edit-${it.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => remove(it.id)} className="h-8 w-8 grid place-items-center border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`product-delete-${it.id}`} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConsignmentsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY_CONSIGNMENT);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => getConsignments().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      setForm((fm) => ({ ...fm, images: [...fm.images, ...urls] }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded.`);
    } catch { toast.error("Upload failed. Use images under 8MB."); }
    finally { setUploading(false); }
  };
  const removeImage = (url) => setForm((fm) => ({ ...fm, images: fm.images.filter((u) => u !== url) }));

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "featured" ? e.target.checked : e.target.value }));

  const reset = () => { setForm(EMPTY_CONSIGNMENT); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required."); return; }
    if (!form.images.length) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    try {
      if (editId) {
        const updated = await updateConsignment(editId, form);
        setItems((arr) => arr.map((x) => (x.id === editId ? updated : x)));
        toast.success("Consignment updated.");
      } else {
        const created = await createConsignment(form);
        setItems((arr) => [created, ...arr.filter((x) => x.id !== created.id)]);
        toast.success("Consignment published.");
      }
      reset();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const edit = (it) => {
    setEditId(it.id);
    setForm({ title: it.title, player: it.player || "", grade: it.grade || "", price: it.price || "", description: it.description || "", status: it.status || "available", images: it.images || [], featured: !!it.featured });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await deleteConsignment(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
      if (editId === id) reset();
      toast.success("Consignment removed.");
    } catch { toast.error("Delete failed."); }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="consignments-admin">
      <div className="lg:col-span-4">
        <form onSubmit={submit} className="border border-[#27272a] p-6 bg-[#121212] sticky top-24" data-testid="consignment-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-semibold">{editId ? "Edit Consignment" : "Add Consignment"}</h3>
            {editId && (
              <button type="button" onClick={reset} className="text-zinc-500 hover:text-white" data-testid="consignment-cancel-edit"><X size={18} /></button>
            )}
          </div>
          <div className="space-y-4">
            <input className={inputClass} placeholder="Title * (e.g. 2023 Bellingham Prizm)" value={form.title} onChange={update("title")} data-testid="consignment-title" />
            <input className={inputClass} placeholder="Player / details (e.g. Jude Bellingham)" value={form.player} onChange={update("player")} data-testid="consignment-player" />
            <div className="grid grid-cols-2 gap-4">
              <input className={inputClass} placeholder="Grade (PSA 10)" value={form.grade} onChange={update("grade")} data-testid="consignment-grade" />
              <input className={inputClass} placeholder="Price (£450 or POA)" value={form.price} onChange={update("price")} data-testid="consignment-price" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Status</label>
              <select className={`${inputClass} cursor-pointer`} value={form.status} onChange={update("status")} data-testid="consignment-status-select">
                <option value="available" className="bg-[#121212]">Available</option>
                <option value="sold" className="bg-[#121212]">Sold</option>
              </select>
            </div>
            <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Description (optional)" value={form.description} onChange={update("description")} data-testid="consignment-description" />
            <div>
              <label className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] cursor-pointer transition-colors">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload Photos"}
                <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={onUpload} data-testid="consignment-upload" />
              </label>
              {form.images.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="consignment-image-previews">
                  {form.images.map((url) => (
                    <div key={url} className="relative h-20 w-20 border border-[#27272a] overflow-hidden group">
                      <img src={url} alt="preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(url)} className="absolute top-0.5 right-0.5 h-5 w-5 grid place-items-center rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors" aria-label="Remove photo" data-testid="consignment-image-remove"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={update("featured")} className="accent-[#ebb323]" data-testid="consignment-featured" />
              Featured
            </label>
          </div>
          <button type="submit" disabled={saving} className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="consignment-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <>{editId ? <Pencil size={14} /> : <Plus size={14} />} {editId ? "Save Changes" : "Publish Consignment"}</>}
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {!items ? (
          <Loader2 className="animate-spin text-gold" />
        ) : !items.length ? (
          <p className="text-zinc-500 font-mono text-sm">No consignments yet. Add your first one on the left — it appears instantly on the website.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="border border-[#27272a] overflow-hidden" data-testid={`consignment-admin-item-${it.id}`}>
                <div className="relative aspect-[4/3] bg-[#0e0e0e]">
                  {it.images?.[0] ? <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-zinc-700"><Tag size={26} /></div>}
                  <span className={`absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-1 font-semibold ${it.status === "sold" ? "bg-red-900/70 text-red-200" : "bg-gold text-black"}`}>{it.status === "sold" ? "Sold" : "Available"}</span>
                  {it.images?.length > 1 && <span className="absolute bottom-2 right-2 font-mono text-[10px] text-zinc-300 bg-black/60 px-2 py-0.5">{it.images.length} photos</span>}
                  {it.featured && <span className="absolute top-3 right-3 bg-gold text-black p-1.5"><Star size={12} /></span>}
                </div>
                <div className="p-4">
                  <h4 className="font-display font-semibold truncate">{it.title}</h4>
                  <p className="font-mono text-[11px] text-zinc-500 uppercase tracking-[0.1em] mt-1">
                    {it.grade} {it.price && `· ${it.price}`}
                  </p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => edit(it)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#3a3a3a] hover:border-gold hover:text-gold py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`consignment-edit-${it.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => remove(it.id)} className="h-8 w-8 grid place-items-center border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`consignment-delete-${it.id}`} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

function SlotsTab() {
  const [slots, setSlots] = useState(null);
  const [config, setConfig] = useState({ setups: [], durations: [] });
  const [form, setForm] = useState({ date: "", start_time: "10:00", duration: 1, setup: "" });
  const [bulk, setBulk] = useState({ start_date: "", days: 7, times: "10:00, 11:00, 14:00, 15:00", duration: 1, setup: "" });
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = () => adminGetSlots().then(setSlots).catch(() => setSlots([]));
  useEffect(() => {
    load();
    getSlotsConfig().then((c) => {
      setConfig(c);
      setForm((f) => ({ ...f, setup: c.setups[0] || "" }));
      setBulk((b) => ({ ...b, setup: c.setups[0] || "" }));
    }).catch(() => {});
  }, []);

  const updateBulk = (k) => (e) =>
    setBulk((b) => ({ ...b, [k]: k === "duration" || k === "days" ? Number(e.target.value) : e.target.value }));

  const submitBulk = async (e) => {
    e.preventDefault();
    const times = bulk.times.split(",").map((t) => t.trim()).filter(Boolean);
    if (!bulk.start_date || !times.length || !bulk.setup) {
      toast.error("Start date, times and setup are required.");
      return;
    }
    setBulkSaving(true);
    try {
      const { created } = await adminBulkCreateSlots({
        start_date: bulk.start_date, days: bulk.days, times, duration: bulk.duration, setup: bulk.setup,
      });
      await load();
      toast.success(created > 0 ? `Opened ${created} slots.` : "No new slots (all existed already).");
    } catch { toast.error("Bulk open failed."); }
    finally { setBulkSaving(false); }
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "duration" ? Number(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time || !form.setup) {
      toast.error("Date, time and setup are required.");
      return;
    }
    setSaving(true);
    try {
      const created = await adminCreateSlot(form);
      setSlots((arr) => [created, ...(arr || [])]);
      toast.success("Slot opened.");
    } catch { toast.error("Could not create slot."); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      const updated = await adminSetSlotStatus(id, status);
      setSlots((arr) => arr.map((s) => (s.id === id ? updated : s)));
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await adminDeleteSlot(id);
      setSlots((arr) => arr.filter((s) => s.id !== id));
      toast.success("Slot removed.");
    } catch { toast.error("Delete failed."); }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="slots-admin">
      <div className="lg:col-span-4">
        <form onSubmit={submit} className="border border-[#27272a] p-6 bg-[#121212] sticky top-24" data-testid="slot-form">
          <h3 className="font-display text-lg font-semibold mb-5">Open A Slot</h3>
          <div className="space-y-4">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={update("date")} data-testid="slot-date" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Start time</label>
              <input type="time" min="09:00" max="19:00" className={inputClass} value={form.start_time} onChange={update("start_time")} data-testid="slot-time" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Duration</label>
              <select className={`${inputClass} cursor-pointer`} value={form.duration} onChange={update("duration")} data-testid="slot-duration">
                {config.durations.map((d) => (
                  <option key={d} value={d} className="bg-[#121212]">{d} hour{d > 1 ? "s" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Setup</label>
              <select className={`${inputClass} cursor-pointer`} value={form.setup} onChange={update("setup")} data-testid="slot-setup">
                {config.setups.map((s) => (
                  <option key={s} value={s} className="bg-[#121212]">{s}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="slot-create">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={14} /> Open Slot</>}
          </button>
        </form>

        <form onSubmit={submitBulk} className="border border-[#27272a] p-6 bg-[#121212] mt-6" data-testid="bulk-slot-form">
          <h3 className="font-display text-lg font-semibold mb-1">Open A Whole Week</h3>
          <p className="text-zinc-500 text-xs mb-5">Release many slots in one click.</p>
          <div className="space-y-4">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Start date</label>
              <input type="date" className={inputClass} value={bulk.start_date} onChange={updateBulk("start_date")} data-testid="bulk-start-date" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Number of days</label>
              <input type="number" min="1" max="31" className={inputClass} value={bulk.days} onChange={updateBulk("days")} data-testid="bulk-days" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Daily start times (comma-separated)</label>
              <input className={inputClass} placeholder="10:00, 14:00" value={bulk.times} onChange={updateBulk("times")} data-testid="bulk-times" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Duration</label>
                <select className={`${inputClass} cursor-pointer`} value={bulk.duration} onChange={updateBulk("duration")} data-testid="bulk-duration">
                  {config.durations.map((d) => (<option key={d} value={d} className="bg-[#121212]">{d}h</option>))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Setup</label>
                <select className={`${inputClass} cursor-pointer`} value={bulk.setup} onChange={updateBulk("setup")} data-testid="bulk-setup">
                  {config.setups.map((s) => (<option key={s} value={s} className="bg-[#121212]">{s}</option>))}
                </select>
              </div>
            </div>
          </div>
          <button type="submit" disabled={bulkSaving} className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-gold text-gold font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-gold hover:text-black transition-colors disabled:opacity-60" data-testid="bulk-create">
            {bulkSaving ? <Loader2 size={15} className="animate-spin" /> : <><CalendarDays size={14} /> Open The Week</>}
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {!slots ? (
          <Loader2 className="animate-spin text-gold" />
        ) : !slots.length ? (
          <p className="text-zinc-500 font-mono text-sm">No slots yet. Open one on the left.</p>
        ) : (
          <div className="space-y-3" data-testid="slots-admin-list">
            {slots.map((s) => (
              <div key={s.id} className="border border-[#27272a] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-slot-${s.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 font-display text-lg font-semibold">
                      <CalendarDays size={16} className="text-gold" /> {fmtDate(s.date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-sm text-zinc-300">
                      <Clock size={13} className="text-gold" /> {s.start_time} · {s.duration}h
                    </span>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${STATUS_STYLES[s.status]}`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">{s.setup}</p>
                  {s.status !== "open" && s.booked_name && (
                    <p className="text-zinc-500 text-xs mt-1 font-mono">
                      {s.booked_name} · {s.booked_email}{s.booked_phone ? ` · ${s.booked_phone}` : ""}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.status !== "open" && (
                    <button onClick={() => setStatus(s.id, "open")} className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`slot-reopen-${s.id}`}>
                      <RotateCcw size={13} /> Reopen
                    </button>
                  )}
                  {s.status === "open" && (
                    <button onClick={() => setStatus(s.id, "closed")} className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`slot-close-${s.id}`}>
                      <X size={13} /> Close
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} className="h-9 w-9 grid place-items-center rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`slot-delete-${s.id}`} aria-label="Delete slot">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


const REQ_STYLES = {
  pending: "bg-gold text-black",
  approved: "bg-green-600 text-white",
  declined: "bg-red-900/60 text-red-300",
};

function RequestsTab() {
  const [items, setItems] = useState(null);

  const load = () => adminGetRequests().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await adminSetRequestStatus(id, status);
      await load();
      toast.success(status === "approved" ? "Approved — session confirmed & customer emailed." : "Request declined.");
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await adminDeleteRequest(id);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Delete failed."); }
  };

  if (!items) return <Loader2 className="animate-spin text-gold" />;
  if (!items.length)
    return <p className="text-zinc-500 font-mono text-sm">No booking requests yet.</p>;

  return (
    <div className="space-y-4" data-testid="requests-list">
      {items.map((r) => (
        <div
          key={r.id}
          className={`border p-6 ${r.status === "pending" ? "border-gold/30 bg-gold/5" : "border-[#27272a] opacity-80"}`}
          data-testid={`request-${r.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-xl font-semibold">{r.name}</h3>
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${REQ_STYLES[r.status]}`}>{r.status}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-gold" />
                  {new Date(r.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                </span>
                <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-gold" /> {r.start_time}</span>
                <span className="font-mono text-xs text-gold">{r.setup}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {r.email}</a>
                {r.phone && <span className="font-mono text-xs">{r.phone}</span>}
              </div>
              {r.note && <p className="text-zinc-300 mt-3 text-sm leading-relaxed max-w-2xl">{r.note}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ReplyButton
                email={r.email}
                name={r.name}
                defaultSubject={`Re: Your studio booking request — ${r.setup || "SEC Studios"}`}
                greeting={`Hi ${r.name || "there"},\n\nThanks for your studio booking request for ${r.setup || "the studio"} on ${new Date(r.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} at ${r.start_time}.\n\n\n\n— SEC Studios`}
                testid={`request-reply-${r.id}`}
              />
              {r.status === "pending" && (
                <>
                  <button onClick={() => setStatus(r.id, "approved")} className="inline-flex items-center gap-1.5 rounded-full border border-green-700 text-green-400 hover:bg-green-700 hover:text-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`request-approve-${r.id}`}>
                    <Check size={13} /> Approve
                  </button>
                  <button onClick={() => setStatus(r.id, "declined")} className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`request-decline-${r.id}`}>
                    <X size={13} /> Decline
                  </button>
                </>
              )}
              <button onClick={() => remove(r.id)} className="h-9 w-9 grid place-items-center rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`request-delete-${r.id}`} aria-label="Delete request">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamTab() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => adminGetTeam().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Email and password are required."); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSaving(true);
    try {
      await adminCreateTeamMember(form);
      await load();
      setForm({ name: "", email: "", password: "" });
      toast.success("Partner login created.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add member.");
    } finally { setSaving(false); }
  };

  const remove = async (email) => {
    try {
      await adminDeleteTeamMember(email);
      setItems((arr) => arr.filter((u) => u.email !== email));
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove member.");
    }
  };

  const inputClass = "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="team-admin">
      <div className="lg:col-span-4">
        <form onSubmit={submit} className="border border-[#27272a] p-6 bg-[#121212] sticky top-24" data-testid="team-form">
          <h3 className="font-display text-lg font-semibold mb-1">Add A Partner</h3>
          <p className="text-zinc-500 text-xs mb-5">They'll be able to log in and manage everything.</p>
          <div className="space-y-4">
            <input className={inputClass} placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="team-name" />
            <input type="email" className={inputClass} placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="team-email" />
            <input type="text" className={inputClass} placeholder="Password * (min 6 chars)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="team-password" />
          </div>
          <button type="submit" disabled={saving} className="w-full mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="team-create">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={14} /> Add Partner</>}
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {!items ? (
          <Loader2 className="animate-spin text-gold" />
        ) : (
          <div className="space-y-3" data-testid="team-list">
            {items.map((u) => (
              <div key={u.email} className="border border-[#27272a] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`team-member-${u.email}`}>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display text-lg font-semibold">{u.name || "Partner"}</span>
                    {u.is_owner && (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] bg-gold text-black px-2 py-0.5"><ShieldCheck size={11} /> Owner</span>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm mt-1 font-mono">{u.email}</p>
                </div>
                {!u.is_owner && u.email !== (user?.email || "").toLowerCase() && (
                  <button onClick={() => remove(u.email)} className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors" data-testid={`team-remove-${u.email}`}>
                    <Trash2 size={13} /> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function OrdersTab() {
  const [items, setItems] = useState(null);
  const [stages, setStages] = useState([]);

  const load = () => adminGetOrders().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    getOrdersConfig().then((c) => setStages(c.stages || [])).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    try {
      const updated = await adminSetOrderStatus(id, status);
      setItems((arr) => arr.map((o) => (o.id === id ? updated : o)));
      toast.success(`Status updated to "${status}" — customer emailed.`);
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await adminDeleteOrder(id);
      setItems((arr) => arr.filter((o) => o.id !== id));
      toast.success("Order deleted.");
    } catch { toast.error("Delete failed."); }
  };

  if (!items) return <Loader2 className="animate-spin text-gold" />;
  if (!items.length) return <p className="text-zinc-500 font-mono text-sm">No customer submissions yet.</p>;

  return (
    <div className="space-y-4" data-testid="orders-admin-list">
      {items.map((o) => (
        <div key={o.id} className="border border-[#27272a] p-6" data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-xl font-semibold">{o.service}</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] bg-gold text-black px-2.5 py-0.5">{o.status}</span>
              </div>
              <p className="text-zinc-300 text-sm mt-2">{o.card_description} {o.quantity > 1 && <span className="text-zinc-600">· x{o.quantity}</span>}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                <span>{o.customer_name}</span>
                <a href={`mailto:${o.customer_email}`} className="inline-flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {o.customer_email}</a>
              </div>
              {o.note && <p className="text-zinc-500 text-sm mt-2 italic">"{o.note}"</p>}
              {o.images?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`admin-order-images-${o.id}`}>
                  {o.images.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer" className="h-16 w-16 border border-[#2a2a2a] overflow-hidden hover:border-gold transition-colors">
                      <img src={url} alt="card" className="h-full w-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ReplyButton
                email={o.customer_email}
                name={o.customer_name}
                defaultSubject={`Re: Your ${o.service || "service"} submission — South East Collectibles`}
                greeting={`Hi ${o.customer_name || "there"},\n\nAn update on your ${o.service || "service"} submission${o.card_description ? ` (${o.card_description})` : ""} — current status: ${o.status}.\n\n\n\n— South East Collectibles`}
                testid={`order-reply-${o.id}`}
              />
              <select
                value={o.status}
                onChange={(e) => setStatus(o.id, e.target.value)}
                className="bg-[#0e0e0e] border border-[#3a3a3a] focus:border-gold text-white text-sm px-3 py-2.5 outline-none cursor-pointer"
                data-testid={`order-status-${o.id}`}
              >
                {stages.map((s) => <option key={s} value={s} className="bg-[#121212]">{s}</option>)}
              </select>
              <button onClick={() => remove(o.id)} className="h-9 w-9 grid place-items-center rounded-full border border-[#3a3a3a] hover:border-red-500 hover:text-red-500 transition-colors" data-testid={`order-delete-${o.id}`} aria-label="Delete order">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

